/** @jsx jsx */
import { useCart, useTranslate } from '@chordcommerce/gatsby-theme-autonomy'
import { Link } from 'gatsby'
import PropTypes from 'prop-types'
import { Fragment } from 'react'
import { Box, Button, Flex, jsx, Spinner, Text } from 'theme-ui'
import CloseIcon from '~/assets/images/icons/close.inline.svg'
import CartFooter from '~/components/Cart/Footer'
import GetWineGiveWine from '~/components/Cart/GetWineGiveWine'
import LineItems from '~/components/Cart/LineItems'
import CartBulkMeter from '~/components/Cart/Meter/Bulk'
import CartShippingMeter from '~/components/Cart/Meter/Shipping'
import PromoCode from '~/components/Cart/PromoCode'
import { useCartFlyout } from '~/hooks/components/use-cart-flyout'
import { useEmptyCart } from '~/hooks/components/use-empty-cart'
import { getDoesCartHaveBulkItems } from '~/utils/bulk'
import {
  getAllCartPromotions,
  getAllCartPromotionsForDisplay,
} from '~/utils/promotions'

const CartContainer = ({
  children,
  displayItemTotal,
  displayPromotions,
  displayShipTotal,
  displayStoreCreditRemainingAfterCapture,
  displayTaxTotal,
  displayTotal,
  displayTotalApplicableStoreCredit,
  giftNote,
  isBulk,
  isGiftCardsOnly,
  itemTotal,
  orderTotalAfterStoreCredit,
  shipTotal,
  totalApplicableStoreCredit,
}) => {
  const { isCartFlyoutOpen, closeCartFlyout } = useCartFlyout()

  return (
    <Fragment>
      <Box
        onClick={() => closeCartFlyout()}
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          bottom: '0',
          left: '0',
          opacity: isCartFlyoutOpen ? '1' : '0',
          pointerEvents: isCartFlyoutOpen ? 'auto' : 'none',
          position: 'fixed',
          right: '0',
          top: '0',
          transition: 'opacity 0.3s ease-in-out',
          visibility: isCartFlyoutOpen ? 'visible' : 'hidden',
          zIndex: '199',
        }}
      />
      <Box
        sx={{
          bottom: '0',
          boxShadow: '0px 5px 15px 0px rgba(0, 0, 0, 0.2)',
          maxWidth: '100%',
          overflow: 'hidden',
          position: 'fixed',
          transform: isCartFlyoutOpen ? 'translateX(0)' : 'translateX(500px)',
          transition: 'transform 0.3s ease-in-out',
          top: '0',
          right: '0',
          width: '460px',
          zIndex: '200',
        }}
      >
        <Flex sx={{ flexDirection: 'column', height: '100%' }}>
          <CartHeader />
          <Flex
            sx={{
              backgroundColor: 'backgroundLight',
              flex: '1',
              flexDirection: 'column',
              overflowY: 'scroll',
            }}
          >
            {!isBulk && !isGiftCardsOnly && (
              <Fragment>
                <CartShippingMeter />
                <GetWineGiveWine />
              </Fragment>
            )}
            {isBulk && <CartBulkMeter />}
            {children}
          </Flex>
          {displayTotal && (
            <Box
              sx={{
                backgroundColor: 'white',
                boxShadow: '0px 0px 15px 0px rgba(0, 0, 0, 0.1)',
                zIndex: '1',
              }}
            >
              <CartFooter
                displayItemTotal={displayItemTotal}
                displayPromotions={displayPromotions}
                displayShipTotal={displayShipTotal}
                displayStoreCreditRemainingAfterCapture={
                  displayStoreCreditRemainingAfterCapture
                }
                displayTaxTotal={displayTaxTotal}
                displayTotal={displayTotal}
                displayTotalApplicableStoreCredit={
                  displayTotalApplicableStoreCredit
                }
                giftNote={giftNote}
                isBulk={isBulk}
                isGiftCardsOnly={isGiftCardsOnly}
                isGiftMessageOverlay={true}
                itemTotal={itemTotal}
                orderTotalAfterStoreCredit={orderTotalAfterStoreCredit}
                shipTotal={shipTotal}
                totalApplicableStoreCredit={totalApplicableStoreCredit}
              />
            </Box>
          )}
        </Flex>
      </Box>
    </Fragment>
  )
}

const CartHeader = () => {
  const translate = useTranslate()
  const { closeCartFlyout } = useCartFlyout()

  return (
    <Box
      bg="backgroundLight"
      py={2}
      sx={{
        borderBottom: '1px solid',
        borderBottomColor: 'greyLight',
        position: 'relative',
        textAlign: 'center',
      }}
    >
      <Text as="h2" variant="title5">
        {translate('cart.title')}
      </Text>
      <Button
        onClick={() => closeCartFlyout()}
        variant="reset"
        id="flyout-close-button"
        aria-label="Close shopping cart"
        sx={{
          bottom: '0',
          position: 'absolute',
          right: '0',
          top: '0',
          width: '64px',
        }}
      >
        <CloseIcon sx={{ height: '16px', width: '16px' }} />
      </Button>
    </Box>
  )
}

const CartLoading = () => {
  return (
    <Flex
      sx={{
        alignItems: 'center',
        backgroundColor: 'backgroundLight',
        color: 'brandTertiary',
        height: '100%',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <Spinner
        size="50"
        sx={{
          position: 'absolute',
          top: 'calc(30% + 30px)',
          '@media (prefers-reduced-motion)': {
            circle: {
              animationIterationCount: 8,
            },
          },
        }}
      />
    </Flex>
  )
}

const CartEmpty = ({ isBulk }) => {
  const translate = useTranslate()
  const { closeCartFlyout } = useCartFlyout()

  return (
    <Flex
      px={[2, 3]}
      sx={{
        backgroundColor: 'backgroundLight',
        height: '100%',
        justifyContent: 'center',
        position: 'relative',
        textAlign: 'center',
      }}
    >
      <Box sx={{ position: 'absolute', top: '30%' }}>
        <Text as="h2" variant="title6" mb={4}>
          {translate('cart.empty')}
        </Text>
        <Box onClick={() => closeCartFlyout()}>
          <Button
            as={Link}
            to={!isBulk ? '/shop/' : '/bulk/'}
            variant="secondary"
            bg="white"
          >
            {translate('cart.shop_button')}
          </Button>
        </Box>
      </Box>
    </Flex>
  )
}

const CartNotEmpty = ({
  allPromotions,
  displayPromotions,
  isBulk,
  lineItems,
}) => {
  const promoCode = allPromotions ? allPromotions.find((p) => p.code) : null

  return (
    <Box
      px={[2, 3]}
      sx={{
        backgroundColor: 'backgroundLight',
        flex: '1',
      }}
    >
      <LineItems items={lineItems} linkProducts={!isBulk} />
      <Box pt={2} pb={5}>
        <PromoCode promotion={promoCode} allPromotions={allPromotions} />
      </Box>
    </Box>
  )
}

const CartFlyout = () => {
  const { cart, isFetching } = useCart()
  const { emptyClubCart } = useEmptyCart()

  const {
    displayItemTotal,
    displayShipTotal,
    displayStoreCreditRemainingAfterCapture,
    displayTaxTotal,
    displayTotal,
    displayTotalApplicableStoreCredit,
    giftNote,
    itemTotal,
    lineItems,
    orderTotalAfterStoreCredit,
    shipTotal,
    totalApplicableStoreCredit,
  } = cart

  if (!lineItems || lineItems.length === 0) {
    const isBulkPage =
      typeof window !== 'undefined' && window.location.pathname.includes('bulk')

    if (isFetching) {
      return (
        <CartContainer isBulk={isBulkPage}>
          <CartLoading />
        </CartContainer>
      )
    }

    return (
      <CartContainer isBulk={isBulkPage}>
        <CartEmpty isBulk={isBulkPage} />
      </CartContainer>
    )
  }

  const allPromotions = getAllCartPromotions(cart)
  const displayPromotions = getAllCartPromotionsForDisplay(allPromotions)

  const isBulk = getDoesCartHaveBulkItems(lineItems)

  const isGiftCardsOnly =
    lineItems.filter((lineItem) => !lineItem.giftCards.length).length === 0

  emptyClubCart()

  return (
    <CartContainer
      displayItemTotal={displayItemTotal}
      displayPromotions={displayPromotions}
      displayShipTotal={displayShipTotal}
      displayStoreCreditRemainingAfterCapture={
        displayStoreCreditRemainingAfterCapture
      }
      displayTaxTotal={displayTaxTotal}
      displayTotal={displayTotal}
      displayTotalApplicableStoreCredit={displayTotalApplicableStoreCredit}
      giftNote={giftNote}
      isBulk={isBulk}
      isGiftCardsOnly={isGiftCardsOnly}
      itemTotal={itemTotal}
      orderTotalAfterStoreCredit={orderTotalAfterStoreCredit}
      shipTotal={shipTotal}
      totalApplicableStoreCredit={totalApplicableStoreCredit}
    >
      <CartNotEmpty
        isBulk={isBulk}
        lineItems={lineItems}
        allPromotions={allPromotions}
        displayPromotions={displayPromotions}
      />
    </CartContainer>
  )
}

CartNotEmpty.propTypes = {
  lineItems: PropTypes.arrayOf(PropTypes.shape({})),
  displayTotal: PropTypes.string,
  displayItemTotal: PropTypes.string,
  displayPromotions: PropTypes.arrayOf(PropTypes.shape({})),
  displayTaxTotal: PropTypes.string,
  displayShipTotal: PropTypes.string,
  displayTotalApplicableStoreCredit: PropTypes.string,
  displayOrderTotalAfterStoreCredit: PropTypes.string,
}

export default CartFlyout
